import { billingSvcApi } from '@ctct/infrastructure-layer';

import { type PromoCodesValidationRequest } from './schemas';
import { BillingStrategy } from '../../types';
import type { Rating } from '../../value-objects';
import { planRepo } from '../plan-repo';
import { zodService } from '../ZodService';

export type PromoCodesValidationResponse = {
  success: boolean;
  messages: { promotionCode: string | null; reasonMessage: string }[];
  rating: Rating | null;
};

class PromoCodesRepo {
  public getPromoCodesValidation = async (body: PromoCodesValidationRequest): Promise<PromoCodesValidationResponse> => {
    if (body.billingStrategy === BillingStrategy.Stripe) {
      return this.getRatingCallBasedPromoCodesValidation(body);
    }

    return this.getHtmlBasedPromoCodesValidation(body);
  };

  private async getHtmlBasedPromoCodesValidation(
    body: Extract<PromoCodesValidationRequest, { billingStrategy: BillingStrategy.InHouse }>
  ) {
    const response = await billingSvcApi.postGetPromoValidation({
      planGroupId: body.planGroupId,
      planId: body.planId,
      baasAddonIds: body.baasAddonIds,
      promotionCodes: body.promotionCodes,
    });

    const data = zodService.safeParseString(response.data);

    // The service returns an extra `<br>`, which we need to strip out.
    const messages = data
      .split(/<br\s*\/?>/)
      .map((message) => {
        // Strip out HTML before passing to React.
        const element = document.createElement('div');

        element.innerHTML = message;

        return element.textContent?.trim();
      })
      .filter((message): message is string => typeof message === 'string' && message.length > 0);

    // HTTP response is always 200 OK, so we need to differentiate success or
    // failure based on the text returned.
    return {
      success: messages.length === 1 && messages[0] === 'success',
      messages: messages.map((message) => ({
        // Historically, the UI has only displayed the error message itself, not the promo code that caused it,
        // so we return `null` here to avoid displaying it.
        promotionCode: null,
        reasonMessage: message,
      })),
      rating: null,
    };
  }

  private async getRatingCallBasedPromoCodesValidation(
    body: Extract<PromoCodesValidationRequest, { billingStrategy: BillingStrategy.Stripe }>
  ): Promise<PromoCodesValidationResponse> {
    try {
      const rating = await planRepo.getPlanRating(body.ratingRequest.toJson());

      const unappliedPromoCodes = rating.getUnappliedPromoCodes();

      if (unappliedPromoCodes.length > 0) {
        // Search for an error message in the _reverse_ order of which code was applied, so that
        // we get the error message for the most recently applied code.
        const requestCodes = [...body.ratingRequest.newPromotionCodes].reverse();

        // eslint-disable-next-line no-restricted-syntax
        for (const requestCode of requestCodes) {
          const unappliedPromoCode = unappliedPromoCodes.find((code) => code.promotionCode === requestCode);

          if (unappliedPromoCode) {
            return {
              success: false,
              messages: [
                { promotionCode: unappliedPromoCode.promotionCode, reasonMessage: unappliedPromoCode.reasonMessage },
              ],
              rating,
            };
          }
        }

        return { success: true, messages: [], rating };
      }

      return { success: unappliedPromoCodes.length === 0, messages: [], rating };
    } catch (error) {
      return {
        success: false,
        messages: [
          {
            promotionCode: null,
            reasonMessage: error instanceof Error ? error.message : 'Unknown error applying promo code',
          },
        ],
        rating: null,
      };
    }
  }
}

export const promoCodesRepo = new PromoCodesRepo();
